import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Menu, X } from 'lucide-react';
import Header from './components/header';
// Portfolio data
import portfolioData from './assets/static-data.json';

// Utility functions
const formatDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) return "";
  const month = dateStr.substring(0, 2);
  const day = dateStr.substring(2, 4);
  const year = dateStr.substring(4);
  return `${month}/${day}/${year}`;
};

// Main App Component
const PortfolioApp = () => {
  const [activeSection, setActiveSection] = useState('about');
  const [navOpen, setNavOpen] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedSkills, setExpandedSkills] = useState({});
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Refs for sections
  const aboutRef = useRef(null);
  const experienceRef = useRef(null);
  const skillsRef = useRef(null);
  const educationRef = useRef(null);
  const publicationsRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Observer for active section in scroll mode
  useEffect(() => {
    if (isDesktop) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const sectionId = entry.target.id;
              setActiveSection(sectionId);
            }
          });
        },
        { threshold: 0.3 }
      );

      const sections = document.querySelectorAll('section[id]');
      sections.forEach((section) => {
        observer.observe(section);
      });

      return () => {
        sections.forEach((section) => {
          observer.unobserve(section);
        });
      };
    }
  }, [isDesktop]);

  const toggleProject = (companyIndex, projectIndex) => {
    const key = `${companyIndex}-${projectIndex}`;
    setExpandedProjects(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleSkillArea = (index) => {
    setExpandedSkills(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleNav = () => {
    setNavOpen(!navOpen);
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    
    if (isDesktop) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    setNavOpen(false);
  };

  // Navigation items with education moved to the end
  const navItems = [
    { id: 'about', label: 'About', ref: aboutRef },
    { id: 'experience', label: 'Experience', ref: experienceRef },
    { id: 'skills', label: 'Skills', ref: skillsRef },
    { id: 'publications', label: 'Publications', ref: publicationsRef },
    { id: 'recognition', label: 'Recognition', ref: recognitionRef },
    { id: 'media', label: 'Media', ref: mediaRef },
    { id: 'education', label: 'Education', ref: educationRef }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-800 text-white p-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">Anujkumarsinh Donvir</h1>
          
          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button onClick={toggleNav} className="text-white p-2">
              {navOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-3 py-2 rounded-md transition-colors ${
                  activeSection === item.id 
                    ? 'bg-blue-900 text-white' 
                    : 'text-blue-100 hover:bg-blue-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Mobile Navigation */}
      {navOpen && (
        <div className="lg:hidden bg-blue-700 text-white">
          <nav className="container mx-auto py-2 flex flex-col">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 text-left ${
                  activeSection === item.id 
                    ? 'bg-blue-600 font-medium' 
                    : 'hover:bg-blue-600'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4 flex-grow">
        {/* If desktop, render all sections; if mobile, render only active section */}
        <div className="hidden md:block">
            <Header />
        </div>
        {/* About Section */}
        <section 
          id="about" 
          ref={aboutRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'about' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">{portfolioData.intro.title}</h2>
          <div className="space-y-4">
            {portfolioData.intro.details.map((detail, index) => (
              <p key={index} className="text-gray-700 leading-relaxed">{detail}</p>
            ))}
          </div>
        </section>

        {/* Experience Section */}
        <section 
          id="experience" 
          ref={experienceRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'experience' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Professional Experience</h2>
          <div className="space-y-8">
            {portfolioData.companies.map((company, companyIndex) => (
              <div key={companyIndex} className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-2xl font-bold text-gray-800">{company.title}</h3>
                <p className="text-lg text-blue-600 font-medium mb-4">{company.position}</p>
                
                <div className="space-y-6 mt-4">
                  {company.projects.map((project, projectIndex) => {
                    const isExpanded = expandedProjects[`${companyIndex}-${projectIndex}`];
                    
                    return (
                      <div key={projectIndex} className="bg-gray-50 rounded-md p-4">
                        <div 
                          className="flex justify-between items-center cursor-pointer"
                          onClick={() => toggleProject(companyIndex, projectIndex)}
                        >
                          <h4 className="text-xl font-semibold text-gray-800">
                            {project.title || `${company.title} Project`}
                          </h4>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                        
                        {isExpanded && (
                          <ul className="mt-4 space-y-2 list-disc list-inside text-gray-700">
                            {project.tasks.map((task, taskIndex) => (
                              <li key={taskIndex} className="ml-4">{task}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section 
          id="skills" 
          ref={skillsRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'skills' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Technical Skills</h2>
          <div className="space-y-8">
            {portfolioData.technicalSkill.map((skillArea, areaIndex) => (
              <div key={areaIndex} className="border rounded-lg p-4">
                <div 
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSkillArea(areaIndex)}
                >
                  <h3 className="text-2xl font-bold text-gray-800">{skillArea.area}</h3>
                  {expandedSkills[areaIndex] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
                
                {expandedSkills[areaIndex] && (
                  <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {skillArea.focus.map((focus, focusIndex) => (
                      <div key={focusIndex} className="bg-gray-50 rounded-md p-4">
                        <h4 className="text-lg font-semibold text-blue-700 mb-2">{focus.areaName}</h4>
                        <div className="flex flex-wrap gap-2">
                          {focus.skillList.map((skill, skillIndex) => (
                            <span 
                              key={skillIndex}
                              className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Publications Section */}
        <section 
          id="publications" 
          ref={publicationsRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'publications' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Publications</h2>
          
          {/* Research Papers */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Research Papers</h3>
            <div className="space-y-4">
              {portfolioData.researchPapers.map((paper, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="text-lg font-semibold text-gray-800">{paper.title}</h4>
                  <p className="text-gray-600">
                    Published: {formatDate(paper.date)}
                  </p>
                  {paper.link && (
                    <a 
                      href={paper.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:underline mt-1"
                    >
                      View Paper <ExternalLink size={16} className="ml-1" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Articles */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Articles</h3>
            <div className="space-y-4">
              {portfolioData.articles.map((article, index) => (
                <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="text-lg font-semibold text-gray-800">{article.title}</h4>
                  <p className="text-gray-600">
                    Published: {formatDate(article.date)}
                  </p>
                  {article.link && (
                    <a 
                      href={article.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:underline mt-1"
                    >
                      Read Article <ExternalLink size={16} className="ml-1" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recognition Section */}
        <section 
          id="recognition" 
          ref={recognitionRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'recognition' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Professional Recognition</h2>
          
          {portfolioData.professionalRecognition.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{section.section}</h3>
              <div className="space-y-4">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="border-l-4 border-yellow-500 pl-4 py-2">
                    {item.link ? (
                      <a href={item.link} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" dangerouslySetInnerHTML={{ __html: item.title }} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: item.title }} />
                    )}
                    
                    {item.events && (
                      <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                        {item.events.map((event, eventIndex) => (
                          <li key={eventIndex} className="ml-4">{event}</li>
                        ))}
                      </ul>
                    )}
                    
                    {item.description && (
                      <p className="text-gray-700 mt-2">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Media Section */}
        <section 
          id="media" 
          ref={mediaRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'media' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Media Showcase</h2>
          <div className="space-y-4">
            {portfolioData.mediaShowcase.map((media, index) => (
              <div key={index} className="border-l-4 border-purple-500 pl-4 py-2">
                <h3 className="text-xl font-semibold text-gray-800">{media.title}</h3>
                {media.link && (
                  <a 
                    href={media.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:underline mt-1"
                  >
                    View Media <ExternalLink size={16} className="ml-1" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Education Section - moved to the end */}
        <section 
          id="education" 
          ref={educationRef}
          className={`bg-white rounded-lg shadow-md p-6 mb-8 scroll-mt-20 ${!isDesktop && activeSection !== 'education' ? 'hidden' : ''}`}
        >
          <h2 className="text-3xl font-bold text-blue-800 mb-6">Education</h2>
          <div className="space-y-6">
            {portfolioData.education.map((edu, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                <h3 className="text-xl font-bold text-gray-800">{edu.degree}</h3>
                <p className="text-lg text-blue-600">{edu.institute}</p>
                <p className="text-gray-700">GPA: {edu.gpa}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 px-4">
        <div className="container mx-auto text-center">
          <p>© {new Date().getFullYear()} Professional Portfolio. All rights reserved.</p>
          <p className="mt-2 text-gray-400">Thought Leader| Tech Lead | AWS Certified</p>
        </div>
      </footer>
    </div>
  );
};

export default PortfolioApp;